import type { ModelMessage, ToolModelMessage, UIMessage } from "ai";
import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

import { Chat } from "@/db/schema";

/**
 * Keep tailwind-merge's conflict model aligned with our custom font-size
 * scale. Without this extension, numeric `text-13` and the size-only
 * `text-size-*` migration tokens are mistaken for unrelated text utilities,
 * so component defaults such as `text-sm` can win in the generated CSS.
 *
 * The tokens live in their own `text-size` group instead of the built-in
 * `font-size` group on purpose: `font-size` also conflicts with `leading`
 * (because `text-sm` etc. set a line-height), but our tokens are size-only
 * and must not delete an earlier `leading-*` class. The two groups conflict
 * with each other in both directions, so later classes still win across
 * vocabularies.
 */
const mergeTailwindClasses = extendTailwindMerge<"text-size">({
  extend: {
    classGroups: {
      "text-size": [
        {
          text: [
            "13",
            {
              size: ["10", "11", "12", "14", "15", "18", "20", "24"],
            },
          ],
        },
      ],
      // Custom named steps of the z-index scale (tailwind.config.ts). The
      // built-in `z` group only recognizes integers/arbitrary values, so
      // without this `cn("z-50", "z-overlay")` keeps BOTH classes and the
      // effective z-index depends on stylesheet order instead of the
      // later class winning (e.g. lifting a dialog above a z-overlay HUD).
      z: [{ z: ["overlay", "toast", "max"] }],
    },
    conflictingClassGroups: {
      "text-size": ["font-size"],
      "font-size": ["text-size"],
    },
  },
});

// Re-export chat request utilities for convenient importing
export {
  withFreshTiming,
  extractVideoOverlayTiming,
  isValidVideoTimingForCaptions,
  cleanChatMessagesForAPI,
  HttpError,
  type GetSerializedTimingFn,
  type WithTimingOptions,
} from "./utils/chat-request";

// Re-export format utilities
export { formatTimeMinSec, clampEndTime } from "./utils/format";

export function cn(...inputs: ClassValue[]) {
  return mergeTailwindClasses(clsx(inputs));
}

interface ApplicationError extends Error {
  info: string;
  status: number;
}

export const fetcher = async (url: string) => {
  const res = await fetch(url);

  if (!res.ok) {
    const error = new Error(
      "An error occurred while fetching the data.",
    ) as ApplicationError;

    error.info = await res.json();
    error.status = res.status;

    throw error;
  }

  return res.json();
};

export function getLocalStorage(key: string) {
  if (typeof window !== "undefined") {
    return JSON.parse(localStorage.getItem(key) || "[]");
  }
  return [];
}

export function generateUUID(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function addToolMessageToChat({
  toolMessage,
  messages,
}: {
  toolMessage: ToolModelMessage;
  messages: Array<UIMessage>;
}): Array<UIMessage> {
  return messages.map((message) => {
    if ((message as any).toolInvocations) {
      return {
        ...message,
        toolInvocations: (message as any).toolInvocations.map(
          (toolInvocation: any) => {
            // Filter for tool result parts that have toolCallId (excludes ToolApprovalResponse)
            const toolResult = toolMessage.content.find(
              (tool) =>
                "toolCallId" in tool &&
                tool.toolCallId === toolInvocation.toolCallId,
            );

            const rawResult =
              toolResult && "output" in toolResult
                ? toolResult.output
                : toolResult && "result" in (toolResult as any)
                  ? (toolResult as any).result
                  : undefined;
            const result =
              rawResult && typeof rawResult === "object" && "value" in rawResult
                ? rawResult.value
                : rawResult;

            if (result !== undefined) {
              return {
                ...toolInvocation,
                state: "result",
                result,
              };
            }

            return toolInvocation;
          },
        ),
      };
    }

    return message;
  });
}

export function convertToUIMessages(
  messages: Array<ModelMessage>,
): Array<UIMessage> {
  return messages.reduce((chatMessages: Array<UIMessage>, message) => {
    if (message.role === "tool") {
      return addToolMessageToChat({
        toolMessage: message as ToolModelMessage,
        messages: chatMessages,
      });
    }

    let textContent = "";
    const toolInvocations: Array<any> = [];

    if (typeof message.content === "string") {
      textContent = message.content;
    } else if (Array.isArray(message.content)) {
      for (const content of message.content) {
        if (content.type === "text") {
          textContent += content.text;
        } else if (content.type === "tool-call") {
          toolInvocations.push({
            state: "call",
            toolCallId: content.toolCallId,
            toolName: content.toolName,
            args: (content as any).input ?? (content as any).args,
          });
        }
      }
    }

    chatMessages.push({
      id: generateUUID(),
      role: message.role,
      content: textContent,
      toolInvocations,
    } as any);

    return chatMessages;
  }, []);
}

export function getTitleFromChat(chat: Chat) {
  // Prefer the persisted title. History list responses intentionally omit
  // message bodies (messages: []), so deriving from messages below would
  // label every conversation "New chat" and make them indistinguishable.
  const storedTitle = typeof chat.title === "string" ? chat.title.trim() : "";
  if (storedTitle && storedTitle !== "New Chat") {
    return storedTitle;
  }

  const messages = Array.isArray(chat.messages) ? chat.messages : [];

  // Build lightweight context from the conversation
  const textSnippets: string[] = [];
  const tools: string[] = [];

  const nonSystemMessages = messages.filter((m) => m.role !== "system");
  for (const m of nonSystemMessages) {
    // collect short text snippets from user/assistant messages
    if (
      (m.role === "user" || m.role === "assistant") &&
      typeof (m as any).content === "string"
    ) {
      const s = (m as any).content.trim();
      if (s) textSnippets.push(s);
    }
    // collect tool names if any
    if (Array.isArray((m as any).toolInvocations)) {
      for (const t of (m as any).toolInvocations) {
        if (t?.toolName) tools.push(String(t.toolName).toLowerCase());
      }
    }
  }

  // If nothing to go on, fallback
  if (textSnippets.length === 0 && tools.length === 0) {
    return "New chat";
  }

  // Domain categories with indicative keywords
  const categories: Array<{ label: string; keys: string[] }> = [
    {
      label: "Trim",
      keys: ["trim", "cut", "clip", "split", "remove", "delete segment"],
    },
    {
      label: "Captions",
      keys: ["caption", "subtitles", "subtitle", "srt", "closed caption"],
    },
    {
      label: "Transcribe",
      keys: ["transcribe", "transcript", "transcription", "asr"],
    },
    {
      label: "Overlay",
      keys: ["overlay", "text", "title", "lower third", "motion", "animation"],
    },
    {
      label: "Export",
      keys: ["export", "download", "save", "render", "encode"],
    },
    {
      label: "Resize",
      keys: [
        "resize",
        "crop",
        "aspect",
        "ratio",
        "1080",
        "4k",
        "vertical",
        "portrait",
        "square",
      ],
    },
    { label: "Speed", keys: ["speed", "slow", "fast", "time-lapse", "fps"] },
    {
      label: "Audio",
      keys: [
        "audio",
        "music",
        "volume",
        "mute",
        "noise",
        "denoise",
        "voiceover",
      ],
    },
    {
      label: "Merge",
      keys: ["merge", "concat", "append", "combine", "stitch"],
    },
  ];

  // Stopwords to ignore when extracting keywords
  const stop = new Set([
    "the",
    "a",
    "an",
    "and",
    "or",
    "to",
    "of",
    "in",
    "on",
    "for",
    "with",
    "by",
    "at",
    "from",
    "as",
    "is",
    "are",
    "was",
    "were",
    "be",
    "been",
    "being",
    "i",
    "you",
    "we",
    "they",
    "he",
    "she",
    "it",
    "this",
    "that",
    "these",
    "those",
    "can",
    "could",
    "should",
    "would",
    "please",
    "make",
    "do",
    "does",
    "did",
    "into",
    "about",
    "over",
    "up",
    "down",
    "my",
    "your",
    "our",
    "their",
    "his",
    "her",
    "its",
    "me",
    "us",
    "them",
  ]);

  const normalize = (s: string) =>
    s
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const corpus = normalize(textSnippets.slice(0, 4).join(" ")); // first few messages for intent
  const words = corpus.split(" ").filter((w) => w && !stop.has(w));

  // Lightweight keyword scoring (term frequency)
  const freq = new Map<string, number>();
  for (const w of words) {
    freq.set(w, (freq.get(w) ?? 0) + 1);
  }
  const sortedKeywords = Array.from(freq.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([w]) => w);

  // Tool-driven hints
  const toolHint = tools.find(Boolean);
  const toolCategory =
    toolHint &&
    categories.find((c) => c.keys.some((k) => toolHint.includes(k)));

  // Category detection from text
  const textCategory = categories.find((c) =>
    c.keys.some((k) => corpus.includes(k)),
  );

  const chosenCategory = toolCategory ?? textCategory ?? null;

  // Compose a concise 3–5 word title
  const capitalize = (s: string) =>
    s.replace(/\b\w/g, (ch) => ch.toUpperCase());

  // Helper to pick up to N keywords that are not redundant with the category label
  const pickKeywords = (limit: number, exclude: string[] = []) => {
    const out: string[] = [];
    for (const k of sortedKeywords) {
      if (out.length >= limit) break;
      if (exclude.some((e) => k.includes(e.toLowerCase()))) continue;
      out.push(k);
    }
    return out;
  };

  if (chosenCategory) {
    const exclude = [
      chosenCategory.label.toLowerCase(),
      ...chosenCategory.keys,
    ];
    const extras = pickKeywords(3, exclude);
    const parts = [chosenCategory.label, ...extras].slice(0, 5);
    return capitalize(parts.join(" "));
  }

  // Fallback to top keywords, or a generic label if none
  if (sortedKeywords.length > 0) {
    const parts = sortedKeywords.slice(
      0,
      Math.min(5, Math.max(3, sortedKeywords.length)),
    );
    return capitalize(parts.join(" "));
  }

  return "New chat";
}
